import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Typography } from '@mui/material';
import { memo } from 'react';

import { shouldShowUOM } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { PropertyCatalogBadge } from 'shared/ui/PropertyCatalogBadge';
import { UnitOfMeasureDisplay } from 'shared/ui/UnitOfMeasureDisplay/UnitOfMeasureDisplay';
import { decodeHtmlEntities } from 'shared/utils/textFormatters/textFormatters';

const TITLE_TEXT_CLASS = 'product-row-title-text';
const TITLE_ARROW_CLASS = 'product-row-title-arrow';

const supportiveTextSx = {
    fontFamily: 'Open Sans',
    fontSize: '12px',
    lineHeight: 'normal',
    letterSpacing: 0,
    color: 'rgba(0, 0, 0, 0.5)',
    whiteSpace: 'nowrap',
} as const;

export interface ProductRowTitleBlockProps {
    title: string;
    brand?: string;
    uom: string;
    qtyPerUom: number;
    isAddedToCatalog?: boolean;
    interactive: boolean;
    onClickTitle?: () => void;
    dimmed?: boolean;
    titleDataTestClass: string;
    brandUomDataTestClass: string;
}

export const ProductRowTitleBlock = memo(
    ({
        title,
        brand,
        uom,
        qtyPerUom,
        isAddedToCatalog = false,
        interactive,
        onClickTitle,
        dimmed = false,
        titleDataTestClass,
        brandUomDataTestClass,
    }: ProductRowTitleBlockProps) => {
        const decodedBrand = brand ? decodeHtmlEntities(brand) : '';
        const showBrand = Boolean(decodedBrand);
        const showUom = shouldShowUOM(uom);
        const showSupportiveRow = showBrand || showUom;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: showSupportiveRow ? '4px' : 0,
                }}
            >
                <Box
                    onClick={interactive ? onClickTitle : undefined}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        maxWidth: '100%',
                        width: '100%',
                        cursor: interactive ? 'pointer' : 'default',
                        opacity: dimmed ? 0.35 : 1,
                        ...(interactive && {
                            [`&:hover .${TITLE_TEXT_CLASS}`]: {
                                color: '#3F6EB7',
                            },
                            [`&:hover .${TITLE_ARROW_CLASS}`]: {
                                opacity: 1,
                            },
                        }),
                    }}
                >
                    {isAddedToCatalog && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                marginRight: '8px',
                                marginTop: '2px',
                            }}
                        >
                            <PropertyCatalogBadge />
                        </Box>
                    )}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-block',
                                maxWidth: '100%',
                                width: 'fit-content',
                                verticalAlign: 'top',
                                overflow: 'hidden',
                            }}
                        >
                            <ChevronRightIcon
                                className={TITLE_ARROW_CLASS}
                                sx={{
                                    float: 'right',
                                    display: 'block',
                                    fontSize: '24px',
                                    color: '#3F6EB7',
                                    opacity: 0,
                                    transition: 'opacity 0.15s',
                                    marginLeft: '0',
                                    marginTop: '-1px',
                                }}
                            />
                            <Typography
                                className={TITLE_TEXT_CLASS}
                                component="span"
                                data-testclass={titleDataTestClass}
                                sx={{
                                    fontFamily: 'Open Sans',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    letterSpacing: '0',
                                    lineHeight: 'normal',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: '2',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    transition: 'color 0.15s',
                                }}
                            >
                                {decodeHtmlEntities(title)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {showSupportiveRow && (
                    <Box
                        data-testclass={brandUomDataTestClass}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            minWidth: 0,
                            opacity: dimmed ? 0.35 : 1,
                        }}
                    >
                        {showBrand && (
                            <Typography
                                component="span"
                                sx={{
                                    ...supportiveTextSx,
                                    fontWeight: 600,
                                }}
                            >
                                {decodedBrand}
                            </Typography>
                        )}
                        {showBrand && showUom && (
                            <Box
                                aria-hidden
                                sx={{
                                    width: '2px',
                                    height: '2px',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        {showUom && (
                            <Typography
                                component="span"
                                sx={{
                                    ...supportiveTextSx,
                                    fontWeight: 400,
                                }}
                            >
                                <UnitOfMeasureDisplay
                                    unitCode={uom}
                                    quantity={qtyPerUom}
                                />
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    },
);

ProductRowTitleBlock.displayName = 'ProductRowTitleBlock';
